/**
 * DashboardHeader — the header actions of a dashboard: Edit / Done, and in edit mode Add widget, Rename and Delete.
 * Demo pages: /analytics and /saas (period select and export buttons in the page header). Template example: NextAdmin
 * `(dashboard)/(home)/page.tsx` header actions. Server routes: edit, add, rename and delete `CanManageDashboards`.
 */
import React, { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { zDashboardUpdate } from '../../generated/api/zod.gen.js';
import { Form, FormActions, TextAreaFormField, TextFormField, useZodForm } from '../../components/form/index.js';
import { Can } from '../../components/auth/Can.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { Modal } from '../../components/ui/Modal.js';
import { Dashboard } from './dashboardApi.js';

export interface DashboardHeaderProps {
  dashboard: Dashboard;
  /** System (H1) dashboards are read-only: no edit affordances, a badge instead. */
  isSystem?: boolean;
  editMode: boolean;
  onToggleEditMode: () => void;
  onAddWidget: () => void;
  onRename: (values: { name?: string; description?: string | null }) => Promise<unknown>;
  onDeleteDashboard: () => void;
  deleting?: boolean;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  dashboard,
  isSystem = false,
  editMode,
  onToggleEditMode,
  onAddWidget,
  onRename,
  onDeleteDashboard,
  deleting = false,
}) => {
  const [renaming, setRenaming] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const form = useZodForm(zDashboardUpdate, {
    defaultValues: { name: dashboard.name, description: dashboard.description ?? '' },
  });

  if (isSystem) return <Badge variant="neutral">System · read-only</Badge>;

  const rename = async (values: { name?: string; description?: string | null }) => {
    await onRename(values);
    setRenaming(false);
  };

  return (
    <>
      <Can policy="CanManageDashboards">
        {editMode && (
          <>
            <Button variant="secondary" size="sm" onClick={() => setRenaming(true)}>
              <Pencil size={14} aria-hidden="true" />
              Rename
            </Button>
            <Button variant="secondary" size="sm" onClick={onAddWidget}>
              <Plus size={14} aria-hidden="true" />
              Add widget
            </Button>
          </>
        )}
      </Can>
      <Can policy="CanManageDashboards">
        {editMode && (
          <Button variant="secondary" size="sm" onClick={() => setConfirmDelete(true)}>
            <Trash2 size={14} className="text-danger" aria-hidden="true" />
            Delete
          </Button>
        )}
      </Can>
      <Can policy="CanManageDashboards">
        <Button variant={editMode ? 'success' : 'primary'} size="sm" onClick={onToggleEditMode}>
          {editMode ? 'Done' : 'Edit'}
        </Button>
      </Can>

      <Modal open={renaming} onClose={() => setRenaming(false)} title="Rename dashboard" maxWidth="md">
        <Form form={form} onSubmit={rename}>
          <div className="space-y-5">
            <TextFormField name="name" label="Name" required />
            <TextAreaFormField name="description" label="Description" rows={3} emptyAs={null} />
            <FormActions submitLabel="Save" onCancel={() => setRenaming(false)} className="md:col-span-1" />
          </div>
        </Form>
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={onDeleteDashboard}
        title="Delete this dashboard?"
        message={`"${dashboard.name}" and its widget layout are removed. This cannot be undone.`}
        confirmLabel="Delete dashboard"
        loading={deleting}
      />
    </>
  );
};
